import { useEffect, useMemo } from 'react';
import styled from 'styled-components';

import DismissibleChip, { DismissibleChipWrapper } from 'components/shared/DismissibleChip';
import FieldLabel from 'components/shared/form/FieldLabel';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import useInfiniteOptions from 'hooks/useInfiniteOptions';
import { StediPayer } from 'models/StediPayer';
import { indexStediPayers, stediPayerQueryKeys } from 'services/api/stediPayers';
import { colors } from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';

const MAX_STATES_LISTED = 5;
// More than the menu shows at once, so it overflows and scrolling can load the next page.
const MIN_UNPICKED_LOADED = 10;

const capitalize = (word: string) => word.charAt(0).toUpperCase() + word.slice(1);

export function payerDetails({ operatingStates, coverageTypes }: StediPayer) {
  const states = operatingStates.length > MAX_STATES_LISTED ? `${operatingStates.length}` : operatingStates.join(', ');

  return [
    states && `States: ${states}`,
    coverageTypes.length > 0 && `Lines: ${coverageTypes.map(capitalize).join(', ')}`,
  ]
    .filter(Boolean)
    .join(' • ');
}

interface Props {
  value: StediPayer[];
  onChange: (payers: StediPayer[]) => void;
  // Offers payers not yet at this facility; without one, those at none of the user's facilities.
  groupId?: string;
}

export default function StediPayerTypeahead({ value, onChange, groupId }: Props) {
  const params = { sortBy: 'search asc', ...(groupId ? { assignableTo: groupId } : { assignable: true }) };
  const directory = useInfiniteOptions({
    queryKey: stediPayerQueryKeys.index(params),
    fetchFn: indexStediPayers,
    params,
  });

  const picked = useMemo(() => new Set(value.map((payer) => payer.id)), [value]);

  // Picks are hidden from the list, so picking can leave too few to scroll; top up instead.
  const { data, hasNextPage, isFetching, fetchNextPage } = directory.options;
  const loaded = data?.pages.flatMap((page) => page.data) ?? [];
  const unpicked = loaded.filter((payer) => !picked.has(payer.id)).length;
  useEffect(() => {
    if (unpicked < loaded.length && unpicked < MIN_UNPICKED_LOADED && hasNextPage && !isFetching) fetchNextPage();
  }, [unpicked, loaded.length, hasNextPage, isFetching, fetchNextPage]);

  return (
    <div>
      <FieldLabel>Search for payer</FieldLabel>
      <Select<StediPayer>
        {...directory}
        dataTestId='stedi-payer-typeahead'
        inputId='stedi-payer-typeahead'
        value={null}
        onChange={(payer) => payer && onChange([...value, payer])}
        getOptionValue={(payer) => payer.id}
        getOptionLabel={(payer) => payer.name}
        formatOptionLabel={(payer, meta) =>
          meta?.context === 'menu' ? (
            <>
              <div>{payer.name}</div>
              <Details>{payerDetails(payer)}</Details>
            </>
          ) : (
            payer.name
          )
        }
        filterOption={({ data }) => !picked.has((data as StediPayer).id)}
        closeMenuOnSelect={false}
        noOptionsMessage={() => 'No matching payer found.'}
        placeholder='Start typing to search for a payer...'
        typeToSearchText='Start typing to search for a payer...'
      />
      {directory.options.isError && (
        <Unavailable data-testid='payer-search-unavailable'>
          Payer search is unavailable right now. Try again in a moment.
        </Unavailable>
      )}
      <Picked>
        <FieldLabel>{`Selected Payers (${value.length}):`}</FieldLabel>
        <Chips>
          {value.map((payer) => (
            <DismissibleChip
              key={payer.id}
              removeProps={{
                'aria-label': `Remove ${payer.name}`,
                onClick: () => onChange(value.filter(({ id }) => id !== payer.id)),
              }}>
              {payer.name}
            </DismissibleChip>
          ))}
        </Chips>
      </Picked>
    </div>
  );
}

const Details = styled.div`
  font-size: 12px;
  color: ${colors.black50};
`;

const Unavailable = styled(BodySmall)`
  margin-top: 4px;
  color: ${colors.black50};
`;

const Picked = styled.div`
  margin-top: 24px;
`;

const Chips = styled(DismissibleChipWrapper)`
  margin-top: 8px;
`;
